import { icon } from "@fortawesome/fontawesome-svg-core";
import { faVideoPlus } from "@fortawesome/pro-solid-svg-icons/faVideoPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor } from "@tinymce/tinymce-react";
import { PropTypes } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import MentionsEditor from "~/components/forms/HtmlEditors/MentionsEditor";
import GlobalContainer from "~/components/global_container";
import AssistModal from "~/components/modals/AiAssist/Modal";
import { NOOP_FALSE } from "~/constants/callbacks";
import {
  APPT_EDITOR_PLUGINS,
  APPT_EDITOR_TOOLBAR,
  IMAGES_UPLOAD_HANDLER,
  TINYMCE_SCRIPT_PATH,
  TINYMCE_SCROLLABLE_UI_OPTIONS,
} from "~/constants/TinyMCE";
import EditorHelpers from "~/helpers/editor_helpers";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { initWebSpellChecker } from "~/lib/web_spell_checker";

const AppointmentEditor = ({
  id = "",
  value = "",
  className = "",
  placeholder = "Enter text",
  onChange = NOOP_FALSE,
  leadId = null,
  linksToInsert = [],
  clearLinksToInsert = NOOP_FALSE,
  replaceValue = null,
  clearReplaceValue = NOOP_FALSE,
}) => {
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [hasCheckedFeature, setHasCheckedFeature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [showAssistModal, setShowAssistModal] = useState(false);
  const editorRef = useRef(null);
  const init = {
    placeholder,
    cache_suffix: "?v=7.9.1",
    license_key: "gpl",
    ...TINYMCE_SCROLLABLE_UI_OPTIONS,
    plugins: APPT_EDITOR_PLUGINS,
    toolbar: APPT_EDITOR_TOOLBAR,
    toolbar_mode: "wrap",
    branding: false,
    menubar: false,
    file_picker_types: "image",
    min_height: 350,
    relative_urls: false,
    remove_script_host: false,
    document_base_url: Rails.baseUrl,
    forced_root_block: "div",
    entity_encoding: "named",
    content_style: "body { font-family: Arial; font-size: 13px; }",
    paste_as_text: false,
    contextmenu: false,
    paste_remove_styles_if_webkit: false,
    statusbar: true,
    images_upload_handler: IMAGES_UPLOAD_HANDLER,
    setup: (editor) => {
      editor.ui.registry.addIcon("videoIcon", icon(faVideoPlus).html[0]);

      editor.ui.registry.addButton("videos", {
        icon: "videoIcon",
        tooltip: "Insert Video",
        onAction: async () => {
          const { mountVideosModal } = await import(
            /* webpackChunkName: "videos-modal" */ "~/components/modals/videos/VideosModal"
          );
          mountVideosModal({
            modalClass: "modal modal-overlay",
            dialogClass: "modal-dialog modal-lg",
            editor,
          });
        },
      });

      EditorHelpers.addVariablesMenu(editor);

      editor.ui.registry.addButton("ai-assist", {
        icon: "ai",
        tooltip: "AI Assist",
        onAction: () => setShowAssistModal(true),
      });

      EditorHelpers.addVisitLink(editor);
    },
  };

  useEffect(() => {
    const handlePosthogReady = () => {
      const isAccessAllowed = checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_MENTIONS_FLAG,
      );
      setIsFeatureEnabled(isAccessAllowed);
      setHasCheckedFeature(true);
    };

    if (window.isPosthogInitialized) {
      handlePosthogReady();
    } else {
      window.addEventListener("posthogReady", handlePosthogReady);
    }

    return () => {
      window.removeEventListener("posthogReady", handlePosthogReady);
    };
  }, []);

  useEffect(() => {
    if (replaceValue != null) {
      setContent(replaceValue);
      clearReplaceValue();
    }
  }, [replaceValue, clearReplaceValue]);

  useEffect(() => {
    if (linksToInsert?.length) {
      let newContent = content;
      linksToInsert.forEach((link) => {
        const { text, href } = link;
        const linkHtml = `<br/><a href="${href}" target="_blank">${text}</a>&nbsp;`;

        newContent += linkHtml;
      });
      setContent(newContent);
      clearLinksToInsert();
    }
  }, [linksToInsert, clearLinksToInsert]);

  const handleEditorChange = (_value, editor) => {
    const newContent = editor.getContent({ format: "html" });

    setContent(newContent);
    onChange(newContent);
  };

  const handleOnInit = (_event, editor) => {
    editorRef.current = editor;
    setLoading(false);

    if (value) {
      editor.setContent(value, { format: "html" });
    }

    initWebSpellChecker(editor.iframeElement);
  };

  return (
    <div className={`${className} position-relative`}>
      {loading && (
        <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white z-5">
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr-1" />
          Loading
        </div>
      )}

      {hasCheckedFeature && isFeatureEnabled ? (
        <MentionsEditor
          id={id}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
          activityType="appointment"
        />
      ) : (
        <Editor
          id={id}
          tinymceScriptSrc={TINYMCE_SCRIPT_PATH}
          scriptLoading={{ async: true }}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
        />
      )}

      {showAssistModal && (
        <AssistModal
          show
          onHide={() => setShowAssistModal(false)}
          editor={editorRef.current}
          editorType="appointment"
          leadId={leadId}
          product={GlobalContainer.product()}
        />
      )}
    </div>
  );
};

AppointmentEditor.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  leadId: PropTypes.number,
  linksToInsert: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  clearLinksToInsert: PropTypes.func,
  replaceValue: PropTypes.string,
  clearReplaceValue: PropTypes.func,
};

export default AppointmentEditor;
